/* jshint esversion: 6 */
/* globals isCMS, angular */
import RepositoryEntry from './RepositoryEntry';
/**
 * Media class represents a media object with various properties and methods to handle media operations.
 */
class Media extends RepositoryEntry {
    /**
     * Constructs a new Media instance.
     * @param {Object} entry - The initial data for the media object.
     * @param {Object} $injector - AngularJS injector service.
     */
    constructor(entry, $injector) {
        super(entry, $injector);
        this.TaskDataService = $injector.get('TaskDataService');
        this.APIService = $injector.get('APIService');
        this.$q = $injector.get('$q');
        this.$http = $injector.get('$http');

        // Define accessible properties with their types
        this.accessables({
            'type': String,
            'mimeType': String,
            'fileSize': Number,
            'comments': String,
            'uploadName': String,
            'height': Number,
            'width': Number,
            'altText': String,
            'includeTranslation':Boolean,
            'forUseOfCandidate': Array
        });

        // Define validators for the media object
        this.validators({
            'MediaEmptyValidator': 'MediaEmptyValidator'
        });

    }

    /**
     * Gets the file extension of the media.
     * @returns {string} The file extension.
     */
    getFileExtension() {
        return this.uploadName.split('.').pop();
    }

    /**
     * Determines the type of the media based on its MIME type.
     * @returns {string} The type of the media.
     */
    getFileType() {
        let mimetype;

        if (this.mimeType.startsWith('video')) {
            mimetype = 'video';
        } else if (this.mimeType.startsWith('audio')) {
            mimetype = 'audio';
        } else if (this.mimeType.includes('x-subrip') || this.mimeType.includes('text/plain')) {
            mimetype = 'subtitle';
        } else if (this.mimeType.includes('application/zip')) {
            mimetype = 'iframe';
        } else if (this.mimeType.includes('application/vnd.geogebra.file')) {
            mimetype = 'geogebra';
        } else {
            mimetype = 'image';
        }
        return mimetype;
    }

    /**
     * Gets the extended media URL.
     * @param {string} taskUUID - The UUID of the task.
     * @returns {string} The extended media URL.
     */
    getExtendedMediaURL(taskUUID) {
        return '/media/' + this.getMediaURL(taskUUID) + '.' + this.getFileExtension();
    }

    /**
     * Gets the extended media URL for a copied container.
     * @param {string} taskUUID - The UUID of the task.
     * @returns {string} The extended media URL for a copied container.
     */
    getExtendedMediaURLCopiedContainer(taskUUID) {
        return 'data/repositories/' + taskUUID + '/media/' + this.getUUID() + '.' + this.getFileExtension();
    }

    /**
     * Gets the media URL.
     * @param {string} taskUUID - The UUID of the task.
     * @returns {string} The media URL.
     */
    getMediaURL(taskUUID) {
        return isCMS ?
            taskUUID + '/' + this.getUUID()
            :
            this.getUUID();
    }

    /**
     * Gets the thumbnail URL.
     * @param {string} taskUUID - The UUID of the task.
     * @param {string} slug - The slug for the thumbnail.
     * @param {string} extension - The file extension for the thumbnail.
     * @returns {string} The thumbnail URL.
     */
    getThumbnailURL(taskUUID, slug, extension) {
        if (this.mimeType.includes('x-subrip') || this.mimeType.includes('text/plain')) {
            return '/images/cms/dummy-subtitle-thumb.jpg';
        }

        if (this.mimeType.includes('svg')) {
            return '/images/cms/dummy-svg-thumb.jpg';
        }

        if (this.mimeType.includes('zip')) {
            return '/images/cms/dummy-iframecontent-thumb.jpg';
        }

        if (this.mimeType.includes('ggb')) {
            return '/images/cms/dummy-ggb-thumb.jpg';
        }

        slug = slug ? slug : '_thumb';
        extension = extension ? extension : 'jpg';

        return '/media/' + taskUUID + '/' + this.getUUID() + slug + '.' + extension;
    }

    /**
     * Gets the iframe URL.
     * @param {string} taskUUID - The UUID of the task.
     * @param {boolean} thumbnail - Whether to get the thumbnail URL.
     * @returns {string|boolean} The iframe URL or false if not applicable.
     * @throws {string} Throws an error if taskUUID is not provided.
     */
    getIframeURL(taskUUID, thumbnail) {
        if (!taskUUID) { throw 'taskUUID is required.'; }

        let thumbnailSlug = '_thumb', //@TODO get thumbnailSlug and thumbnailExtension values from django settings
            thumbnailExtension = 'jpg';

        let re = /(?:\.([^.]+))?$/,
            uploadName = this.uploadName,
            extension = thumbnail ? thumbnailExtension : re.exec(uploadName)[1],
            mimeType = this.mimeType,
            fileNameSlug = thumbnail ? thumbnailSlug : "",
            fileName = this.getUUID() + fileNameSlug + '.' + extension,
            url = false;

        if (mimeType === 'image/svg+xml') {
            fileName = this.getUUID() + fileNameSlug + '.svg';
            url = isCMS ? "/media/" + taskUUID + "/" + fileName : "/media/" + fileName;
        } else if (mimeType === 'application/zip') {
            url = isCMS ?
                "/iframe/" + taskUUID + "/" + this.getUUID() + "/index.html"
                :
                "/media/iframe-content/" + this.getUUID() + "/index.html";
        }

        return url;
    }

    /**
     * Gets the secure URL for the media.
     * @param {string} taskUUID - The UUID of the task.
     * @returns {Promise} A promise that resolves with the secure URL.
     */
    getSecureURL(taskUUID) {
        let self = this,
            urlDeferred = this.$q.defer();

        if (!isCMS) {
            let url = self.getExtendedMediaURL(taskUUID);
            urlDeferred.resolve(url);
        } else {
            this.APIService.getSecureLink(taskUUID, self.getUUID()).then(function (response) {
                let url = response.data;

                if (angular.isDefined(url) && url) {
                    urlDeferred.resolve(url);
                } else {
                    console.error("Not a valid media URL:");
                    urlDeferred.reject();
                }
            });
        }

        return urlDeferred.promise;
    }

    /**
     * Gets the base64 encoded data URL for the media.
     * @param {string} taskUUID - The UUID of the task.
     * @returns {Promise} A promise that resolves with the base64 data URL.
     */
    getBase64(taskUUID) {
        const self = this;
        const deferred = this.$q.defer();

        this.getSecureURL(taskUUID).then(function(secureUrl) {
            self.$http.get(secureUrl, {responseType: 'arraybuffer'})
                .then(function(response) {
                    const base64 = btoa(
                        new Uint8Array(response.data)
                            .reduce((data, byte) => data + String.fromCharCode(byte), '')
                    );
                    const dataUrl = 'data:' + self.mimeType + ';base64,' + base64;
                    deferred.resolve(dataUrl);
                })
                .catch(function(error) {
                    console.error('Error fetching image:', error);
                    deferred.reject(error);
                });
        }).catch(function(error) {
            console.error('Error getting secure URL:', error);
            deferred.reject(error);
        });

        return deferred.promise;
    }
}

export default Media;